import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Container,
    Typography,
    CircularProgress,
    Alert,
    Box,
    Button,
    TextField,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Select,
    MenuItem,
    FormHelperText,
    InputLabel,
    FormControl,
    Link,
    Tooltip,
    useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useCart } from "../../context/cartContext";
import { useSite } from "../../context/siteContext";

import "./SingleProduct.css";
import { ASSETS_BASE_URL, EXTERNAL_2D_ENABLED, SERVER_BASE_URL, RSPL_LIST, DOCUMENT_EXTERNAL_ENABLED, DOCUMENT_EXTERNAL_URL } from "../../config";
import { useAuth } from "../../context/authContext";
import Swal from "sweetalert2";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import PageNotFound from "../PageNotFound/PageNotFound";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import DescriptionIcon from "@mui/icons-material/Description";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TopProductListItem from "../../components/TopProductListItem/TopProductListItem";
import { UI_TEXT_HELPERS } from "../../constants";
import { useTranslation } from "react-i18next";
import formatCurrency from "../../utilities/currency";
import ModalWrapper from "../../components/ModalWrapper/ModalWrapper";
import { getMachinesByIds, getSingleProduct } from "../../services/breadCrumbsService";
import { getProductsByMachineId, normalizeMachineProductsResponse } from "../../services/machineCardService";
import { getCartRecommendations } from "../../services/cartService";
import { getRecommendationsByUserAndSite } from "../../services/recommendationsService";
import { addProductsToRecommendationList } from "../../services/rsplService";
import { createRecommendation } from "../../services/recommendationsService";
import { saveProductForLater, saveRequestList } from "../../services/productCardService";
import { processModelFile, verifyResourceExists } from "../../services/modelService";
import { toast } from "../../context/ToastContext";
import BomPathView from "../../components/BomViewer/BomPathView";
import { indexBomTree, buildPathTreeFromProductId } from "../../utilities/bomUtils";
import MultipleModelViewer from "../../components/MultipleModelViewer/MultipleModelViewer";
import {
    countAvailableModelsFromModelData,
    extractFilename,
    hasDirectMachineModelsFromModelData,
    selectBestDirectModelFromModelData,
} from "../../utilities/selectBestModel";
import { getSavedForLaterBySiteId, removeFromSavedForLater } from "../../services/savedForLetterService";
import { useOptions } from "../../context/optionContext";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import { buildRsplCodeToLabelMap, getRsplLabelForCode, parseRsplList } from "../../utilities/rspl";
import {
    buildExternalLoginUrl,
    buildExternalTargetUrl,
    getMachineDocEntry,
    openExternalDocumentation,
} from "../../utilities/externalDocs";
import {
    formatOrderQuantity,
    getMinimumSalesOrderQuantity,
    getMultipleSalesOrderQuantity,
    normalizeOrderQuantity,
    parseOrderQuantity,
} from "../../utilities/orderQuantity";

const formatInlineLabel = (label) => `${String(label || "").replace(/[:\s]+$/g, "")}:`;
const isTruthyFlag = (value) =>
    value === true || value === "true" || value === "Y" || value === "1" || value === 1;

/**
 * Part detail page: product info, replacement alert, pricing and cart actions, BOM path,
 * recommendations, documentation and the machine's model launcher. Passes the part's BOM path to
 * the models modal so the model containing this part is highlighted.
 *
 * @returns {JSX.Element}
 */
const SingleProduct = ({
    machineID: machineIdProp,
    productID: productIdProp,
    isModalView = false,
}) => {
    const { optionKeys } = useOptions();
    const resolvedRsplList = useMemo(
        () => parseRsplList(optionKeys?.rsplList, RSPL_LIST),
        [optionKeys?.rsplList]
    );
    const rsplMapping = useMemo(
        () => buildRsplCodeToLabelMap(resolvedRsplList),
        [resolvedRsplList]
    );
    const params = useParams();
    const machineID = machineIdProp ?? params?.machineID;
    const productID = productIdProp ?? params?.productID;
    const { addToCart, cartItems } = useCart(); // Added cartItems
    const { selectedSite, siteData, selectedSiteCurrency } = useSite();
    const theme = useTheme();
    const [price, setPrice] = useState(0);
    const [product, setProduct] = useState(null);
    const [replacementProduct, setReplacementProduct] = useState(null);
    const [replacedByProduct, setReplacedByProduct] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    // Bumped by "chip:navigate" so a same-site/same-part re-ask retries the fetches below even
    // though productID/machineID/selectedSite never actually changed. Mirrors MyMachines.js's
    // identical refreshTick pattern.
    const [refreshTick, setRefreshTick] = useState(0);
    useEffect(() => {
        const onChipNavigate = (e) => {
            if (!String(e?.detail?.path || "").startsWith("/machines")) return;
            setRefreshTick((n) => n + 1);
        };
        window.addEventListener("chip:navigate", onChipNavigate);
        return () => window.removeEventListener("chip:navigate", onChipNavigate);
    }, []);

    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingSFL, setIsLoadingSFL] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isSuccessSFL, setIsSuccessSFL] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [lists, setLists] = useState([]);
    const [selectedList, setSelectedList] = useState("");
    const [newListName, setNewListName] = useState("");
    const [newListDescription, setNewListDescription] = useState("");
    const [newListError, setNewListError] = useState("");
    const [selectListError, setSelectListError] = useState("");
    const [listCreationLoading, setListCreationLoading] = useState(false);
    const [hasMultipleModels, setHasMultipleModels] = useState(false);
    const [machineModelData, setMachineModelData] = useState({});
    const [machineDetailsLoading, setMachineDetailsLoading] = useState(true);
    const [machine_name, setmachine_name] = useState([]);
    const [machine_number, setmachine_number] = useState([]);
    const [machineSerialForDocs, setMachineSerialForDocs] = useState("");
    const [machineHasDocumentation, setMachineHasDocumentation] = useState(false);
    const [m3DButtonLoading, setM3DButtonLoading] = useState(false);
    const [modelModalOpen, setModelModalOpen] = useState(false);
    const [orderCount, setOrderCount] = useState(10);
    const [recommendedProducts, setRecommendedProducts] = useState([]);
    const [recommendationsLoading, setRecommendationsLoading] = useState(false);
    const [bomTree, setBomTree] = useState([]);
    const [bomPathTree, setBomPathTree] = useState([]);
    const [bomPathLoading, setBomPathLoading] = useState(false);
    const [bomPathError, setBomPathError] = useState(false);
    const {
        user,
        hasPermission,
        canViewProductPrice,
        canAddToCart,
        canAddToRequestList,
    } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { stockImageUrl } = useBrandingAssets();
    const maxQtyAllowed = useMemo(() => {
        const parsed = parseInt(optionKeys?.maxOrderQty, 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 999;
    }, [optionKeys]);
    const MAX_QTY = maxQtyAllowed;
    const { flatNodes: bomFlatNodes, nodeMap: bomNodeMap, productIdToNodeId } = useMemo(
        () => indexBomTree(bomTree),
        [bomTree]
    );
    const { selectedSiteData } = useSite();
    const triggerToast = (message, severity = "success") => {
        if (!message) return;
        toast.trigger({ message, severity });
    };
    const locationIdForDocs =
        selectedSiteData?.customer_site_number ||
        selectedSiteData?.customer_site_name ||
        selectedSite ||
        "";
    const machineDocEntry = useMemo(
        () =>
            getMachineDocEntry({
                locationId: locationIdForDocs,
                serialNumber: machineSerialForDocs,
            }),
        [locationIdForDocs, machineSerialForDocs]
    );
    const shouldShowDocumentationButton = DOCUMENT_EXTERNAL_ENABLED
        ? machineDocEntry?.HasDoc === "Y"
        : machineHasDocumentation;

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        const getMachinesSavedForLater = async () => {
            try {
                const response = await getSavedForLaterBySiteId(selectedSite);
                if (response?.status && Array.isArray(response.data)) {
                    const matchFound = response.data.some(
                        (item) =>
                            Number(item.machine_id) === Number(replacementProduct?.replacement_machine_id || machineID) &&
                            Number(item.product_id) === Number(replacementProduct?.replacement_product_id || productID)
                    );
                    setIsSuccessSFL(matchFound);
                }
            } catch (error) {
                console.error("Error fetching saved for later:", error);
            }
        };
        getMachinesSavedForLater();
    }, [machineID, productID, replacementProduct, selectedSite]);
    const canAddToList = hasPermission("recommendations_management.add_parts")
    const canCreateList = hasPermission("recommendations_management.create_list")
    // Part Description hidden: products.product_description is synced from D365 and
    // duplicates what is already shown on the page. Uncomment to bring the section back.
    // const partDescription = product?.product_description?.trim() || "";
    const replacementPurchaseTarget = useMemo(() => {
        if (!replacementProduct?.replacement_product_id) return null;
        const currencyKey = `replacement_price_${String(selectedSiteCurrency || "").toLowerCase()}`;
        const replacementPrice = replacementProduct?.[currencyKey];
        return {
            product_id: replacementProduct.replacement_product_id,
            product_number: replacementProduct.replacement_product_number,
            product_name: replacementProduct.replacement_product_name || replacementProduct.replacement_product_number,
            machine_id: replacementProduct.replacement_machine_id || machineID,
            price: Number(replacementPrice) || 0,
            purchasable: Number(replacementProduct.replacement_purchasable) === 1,
            minimum_sales_order_quantity: replacementProduct.replacement_minimum_sales_order_quantity,
            multiple_sales_order_quantity: replacementProduct.replacement_multiple_sales_order_quantity,
        };
    }, [machineID, replacementProduct, selectedSiteCurrency]);
    const purchaseTarget = replacementPurchaseTarget || product;
    const purchaseTargetPrice = replacementPurchaseTarget ? replacementPurchaseTarget.price : price;
    const minimumSalesOrderQuantity = useMemo(
        () => getMinimumSalesOrderQuantity(purchaseTarget),
        [purchaseTarget]
    );
    const multipleSalesOrderQuantity = useMemo(
        () => getMultipleSalesOrderQuantity(purchaseTarget),
        [purchaseTarget]
    );
    const effectiveMaxQty = useMemo(
        () => Math.max(MAX_QTY, minimumSalesOrderQuantity, normalizeOrderQuantity(MAX_QTY, purchaseTarget)),
        [MAX_QTY, minimumSalesOrderQuantity, purchaseTarget]
    );
    const alternativePartNumber =
        replacementProduct?.replacement_product_number ||
        product?.alternative_item_number ||
        product?.alternativeitemnumber ||
        "";
    const replacedByPartNumber =
        replacedByProduct?.replaced_product_number ||
        "";
    const canPurchaseTarget = replacementPurchaseTarget
        ? Number(replacementPurchaseTarget.purchasable) === 1
        : !alternativePartNumber && Number(product?.purchasable) === 1;
    const canShowPrice = canViewProductPrice();
    const canShowAddToCart = canAddToCart();
    const canShowAddToRequestList = canAddToRequestList();
    const canShowPurchaseActions = canPurchaseTarget && canShowAddToCart;
    const canShowRequestActions = !canPurchaseTarget && canShowAddToRequestList;
    const canShowProductActions = canShowPurchaseActions || canShowRequestActions;

    const productImage = useMemo(() => {
        const raw = product?.asset_data?.images?.[0];
        if (raw) {
            if (/^(https?:)?\/\//.test(raw) || raw.startsWith("data:")) return raw;
            const path = raw.startsWith("/") ? raw : `/${raw}`;
            return `${ASSETS_BASE_URL}${path}`;
        }
        return `${ASSETS_BASE_URL}/ProductImages/TMB/${product?.product_number}.JPG`;
    }, [product]);

    const bestModel = useMemo(
        () => selectBestDirectModelFromModelData(machineModelData),
        [machineModelData]
    );


    // CORRECTED FILTER FUNCTION - This fixes the FBT filtering issue
    const filterRecommendationsNotInCart = useCallback((recommendations, cartItems) => {
        if (!cartItems || cartItems.length === 0) return recommendations;

        // Get product IDs from cart items based on your actual cart structure
        const cartProductIds = cartItems
            .filter(item => item.Site === selectedSite) // Only current site items
            .map(item => item?.ProductID || item?.product_id)
            .filter(Boolean)
            .map((id) => String(id));

        const filtered = recommendations.filter(product =>
            !cartProductIds.includes(String(product.product_id))
        );
        return filtered;
    }, [selectedSite]);

    useEffect(() => {
        if (!productID || !machineID || !selectedSite) {

            return;
        }

        const fetchAllData = async () => {
            try {
                setLoading(true);
                setReplacementProduct(null);
                setReplacedByProduct(null);
                const reqBody = {
                    machine_id: machineID,
                    product_id: productID,
                    site_number: selectedSite
                };

                // Start both requests in parallel
                const productPromise = getSingleProduct(reqBody);

                // Create recommendations request promise but don't await it yet
                setRecommendationsLoading(true);
                const recommendationsPromise = getCartRecommendations([productID], selectedSite, machineID);

                // Wait for product data first (needed for UI)
                const productResponse = await productPromise;

                if (productResponse.status && productResponse.data?.data.length > 0) {

                    const productData = productResponse.data.data[0];

                    setProduct(productData);

                    setReplacementProduct(
                        productResponse.data.replacement ||
                        productData?.replacement ||
                        null
                    );

                    setReplacedByProduct(
                        productResponse.data.replaced_by ||
                        productData?.replaced_by ||
                        null
                    );
                    if (productResponse.data.order_data) {
                        const filteredOrders = productResponse.data.orders
                            .filter((order) => order.customer_site_number === selectedSite)
                            .map((order) => order.order_id);
                        setOrderCount(filteredOrders.length);
                    } else {
                        setOrderCount(0);
                    }
                    setLoading(false);
                    setError(null);

                    // Now handle recommendations in parallel with initial filtering
                    try {
                        const recommendationsResponse = await recommendationsPromise;

                        const recommendations = Array.isArray(recommendationsResponse?.data)
                            ? recommendationsResponse.data
                            : [];

                        setRecommendedProducts(recommendations);
                    } catch (recError) {
                        console.error("Failed to fetch recommendations:", recError);
                        setRecommendedProducts([]);
                    }
                } else {
                    setReplacementProduct(null);
                    setReplacedByProduct(null);
                    setError(t('GENERAL.FAILED_TO_FETCH_DATA_NO_PRODUCT_FOUND'));
                    setOrderCount(0);
                    setLoading(false);
                }
            } catch (err) {
                setReplacementProduct(null);
                setReplacedByProduct(null);
                setError(t('GENERAL.FAILED_TO_FETCH_DATA'));
                setOrderCount(0);
                setLoading(false);
            } finally {
                setRecommendationsLoading(false);
            }
        };

        fetchAllData();
    }, [productID, machineID, selectedSite, t, refreshTick]); // Added cartItems to dependencies

    useEffect(() => {
        if (!machineID || !selectedSite) {
            setBomTree([]);
            return;
        }
        let ignore = false;
        const fetchBomData = async () => {
            setBomPathLoading(true);
            setBomPathError(false);
            try {
                const response = await getProductsByMachineId({
                    machine_id: machineID,
                    site_number: selectedSite,
                });
                if (ignore) return;
                if (response.status) {
                    const { bomTree } = normalizeMachineProductsResponse(response);
                    setBomTree(bomTree || []);
                } else {
                    setBomTree([]);
                    setBomPathError(true);
                }
            } catch (error) {
                if (!ignore) {
                    setBomTree([]);
                    setBomPathError(true);
                }
            } finally {
                if (!ignore) {
                    setBomPathLoading(false);
                }
            }
        };
        fetchBomData();
        return () => {
            ignore = true;
        };
    }, [machineID, selectedSite, refreshTick]);

    useEffect(() => {
        if (!productID) {
            setBomPathTree([]);
            return;
        }
        const pathTree = buildPathTreeFromProductId(
            productID,
            bomFlatNodes,
            bomNodeMap,
            productIdToNodeId
        );
        setBomPathTree(pathTree);
    }, [productID, bomFlatNodes, bomNodeMap, productIdToNodeId]);

    /**
     * Product numbers along the part's BOM path (machine-level assembly down to the part itself),
     * handed to the models modal so sub-assembly models on that path get the "Contains this part"
     * badge. Memoised because the modal keys a lookup on it.
     */
    const highlightProductNumbers = useMemo(() => {
        const numbers = [];
        const walk = (nodes = []) =>
            nodes.forEach((node) => {
                if (node?.productNumber) numbers.push(node.productNumber);
                walk(node?.children || []);
            });
        walk(bomPathTree);
        return numbers;
    }, [bomPathTree]);

    useEffect(() => {
        const fetchMachineDetails = async () => {
            if (!machineID) return;

            setMachineDetailsLoading(true);
            try {
                const reqBody = {
                    machines: [machineID],
                };

                const response = await getMachinesByIds(reqBody);

                if (response.status && Array.isArray(response.data) && response.data.length > 0) {
                    const machineData = response.data[0] || {};

                    setmachine_name(machineData.machine_name || "");
                    setmachine_number(machineData.machine_number || "");

                    setMachineSerialForDocs(
                        machineData.machine_product_name ||
                        machineData.machine_serial_number ||
                        machineData.machine_number ||
                        machineID ||
                        ""
                    );

                    setMachineHasDocumentation(isTruthyFlag(machineData.has_document));

                    const rawModelData = machineData.model_data;
                    let parsedModelData = {};

                    if (rawModelData && typeof rawModelData === "string") {
                        try {
                            const parsed = JSON.parse(rawModelData);
                            parsedModelData = parsed && typeof parsed === "object" ? parsed : {};
                        } catch {
                            parsedModelData = {};
                        }
                    } else if (rawModelData && typeof rawModelData === "object") {
                        parsedModelData = rawModelData;
                    }

                    setMachineModelData(parsedModelData);
                } else {
                    setmachine_name("");
                    setmachine_number("");
                    setMachineSerialForDocs("");
                    setMachineHasDocumentation(false);
                    setMachineModelData({});
                    setHasMultipleModels(false);
                    setError("Machine not found or no longer available.");
                }
            } catch (err) {
                setmachine_name("");
                setmachine_number("");
                setMachineSerialForDocs("");
                setMachineHasDocumentation(false);
                setMachineModelData({});
                setHasMultipleModels(false);
                setError("Failed to fetch machine data.");
            } finally {
                setMachineDetailsLoading(false);
                // Marks model data as resolved for THIS machineID (see the matching
                // Products.js fix and the auto-open effect below for why gating on
                // machineDetailsLoading alone isn't sufficient).
                modelDataForMachineIdRef.current = machineID;
            }
        };

        fetchMachineDetails();
    }, [machineID]);

    // Determine model availability based on cache (machineModelData)
    useEffect(() => {
        try {
            const md = machineModelData || {};
            const hasDirectModels = hasDirectMachineModelsFromModelData(md);
            const modelCount = countAvailableModelsFromModelData(md);
            setHasMultipleModels(hasDirectModels && modelCount > 1);
        } catch (e) {
            setHasMultipleModels(false);
        }
    }, [machineModelData]);

    // CORRECTED useEffect for price and cart filtering
    useEffect(() => {
        if (product && selectedSiteCurrency) {
            const key = `price_${selectedSiteCurrency}`;
            const currencyPrice = product[key];
            const finalPrice = Number(product.purchasable) === 1 ? currencyPrice : 0;
            setPrice(finalPrice);
        }

        // Re-filter recommendations when cart changes
        // if (cartItems && recommendedProducts.length > 0) {
        //     const filtered = filterRecommendationsNotInCart(recommendedProducts, cartItems);
        //     // Only update if the filtered list is different
        //     if (filtered.length !== recommendedProducts.length) {
        //         setRecommendedProducts(filtered);
        //     }
        // }
    }, [product, selectedSiteCurrency]); // Use cartItems instead of cart

    useEffect(() => {
        const fetchLists = async () => {
            const site_id = Array.isArray(siteData)
                ? siteData.find((i) => i.customer_site_number === selectedSite)?.site_id
                : null;
            const user_id = user?.user_id;
            if (!site_id || !user_id) return;

            try {
                const response = await getRecommendationsByUserAndSite(user_id, site_id);

                if (response.status) {
                    let recommendations = [];

                    recommendations = response.data.filter((item) => item.user_id === user_id);

                    if (recommendations.length === 0) setSelectedList("new");
                    else setSelectedList(recommendations[0].id);
                    setLists(recommendations);
                }
            } catch (error) {
                console.error("Error fetching lists:", error);
            }
        };

        if (selectedSite) fetchLists();
    }, [selectedSite, siteData, user?.user_id]);

    useEffect(() => {
        if (!dialogOpen) return;
        if (lists.length === 0) {
            setSelectedList("new");
        } else {
            setSelectedList(lists[0].id);
        }
        setNewListName("");
        setNewListDescription("");
        setNewListError("");
        setSelectListError("");
    }, [dialogOpen, lists]);

    // All your existing handler functions remain the same...
    const handleAddToRecommendationList = async () => {
        if (selectedList !== "new") {
            try {
                const response = await addProductsToRecommendationList(selectedList, [{
                    product_id: purchaseTarget?.product_id,
                    machine_id: purchaseTarget?.machine_id || machineID,
                }]);

                if (response.status) {
                    handleCloseDialog();
                    if (response.data[0].status === "exists") {
                        Swal.fire({
                            icon: "info",
                            title: t('ALERT_MESSAGES.INFO'),
                            text: t('ALERT_MESSAGES.PART_ALREADY_PRESENT_LIST'),
                        });
                    } else {
                        Swal.fire({
                            icon: "success",
                            title: t('ALERT_MESSAGES.SUCCESS'),
                            text: t('ALERT_MESSAGES.PART_ADDED_TO_LIST'),
                        });
                    }
                } else {
                    console.error("Failed to add part to list");
                }
            } catch (error) {
                if (error.response.status === 409) {
                    setSelectListError(t('ALERT_MESSAGES.PART_ALREADY_ADDED_LIST'));
                }
                console.error("Error adding part to list:", error);
            }
        } else if (newListName) {
            setListCreationLoading(true); // Set loading true
            const site_id = siteData.filter((i) => i.customer_site_number === selectedSite)[0]?.site_id;
            const user_id = user.user_id;

            try {
                const duplicateList = lists.some(
                    (list) =>
                        list.name.trim().toLowerCase() === newListName.trim().toLowerCase() &&
                        list.user_id === user_id &&
                        list.site_id === site_id
                );

                if (duplicateList) {
                    setNewListError(t('ALERT_MESSAGES.LIST_NAME_ALREADY_EXISTS')); // Use translation key
                    setListCreationLoading(false); // Reset loading
                    return;
                }
                const response = await createRecommendation({
                    name: newListName,
                    description: newListDescription || "",
                    user_id,
                    site_id,
                });

                if (response.status) {
                    const newListId = response.data.id;
                    const createdList = {
                        id: newListId,
                        name: response.data.name || newListName,
                        description: response.data.description || newListDescription || "",
                        user_id,
                        site_id,
                    };
                    setLists((prev) => {
                        const exists = prev.some((l) => String(l.id) === String(newListId));
                        if (exists) return prev;
                        return [createdList, ...prev];
                    });
                    setSelectedList(newListId);
                    // Assuming addProductsToRecommendationList also returns a status or throws
                    const addProductResponse = await addProductsToRecommendationList(newListId, [{
                        product_id: purchaseTarget?.product_id,
                        machine_id: purchaseTarget?.machine_id || machineID,
                    }]);

                    if (addProductResponse.status) {
                        handleCloseDialog();
                        Swal.fire({
                            icon: "success",
                            title: t('ALERT_MESSAGES.SUCCESS'),
                            text: t('ALERT_MESSAGES.NEW_RECOMMENDATION_LIST'),
                        });
                    } else {
                        console.error("Failed to add product to the new list");
                        // If addProductResponse fails, you might want to delete the newly created list
                        // or at least inform the user better. For now, just error.
                    }
                } else {
                    // Handle backend error from createRecommendation
                    setNewListError(t('ALERT_MESSAGES.FAILED_TO_CREATE_THE_LIST'));
                    console.error("Failed to create the list");
                }
            } catch (error) {
                // Handle network errors or other unexpected errors
                if (error.response && error.response.status === 409) {
                    setNewListError(t('ALERT_MESSAGES.LIST_NAME_ALREADY_EXISTS'));
                } else {
                    setNewListError(t('ALERT_MESSAGES.ERROR_CREATING_LIST'));
                }
                console.error("Error creating new list:", error);
            } finally {
                setListCreationLoading(false); // Always reset loading
            }
        }
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedList("");
        setNewListName("");
        setNewListDescription("");
        setNewListError("");
    };

    const handleListChange = (event) => {
        setSelectListError();
        setSelectedList(event.target.value);
    };

    const handleNewListNameChange = (event) => {
        setNewListName(event.target.value);
        setNewListError("");
    };

    const handleNewListDescriptionChange = (event) => {
        setNewListDescription(event.target.value);
    };

    const showModelError = () =>
        Swal.fire(t('ALERT_MESSAGES.ERROR'), t('GENERAL.FAILED_TO_OPEN_MODEL'), "error");

    const openUrlIfExists = async (url) => {
        if (!url) return false;
        const ok = await verifyResourceExists(url);
        if (!ok) return false;
        window.open(url);
        return true;
    };

    const build2dUrl = (pathValue) => {
        if (!pathValue) return "";
        let normalized = String(pathValue).trim();
        if (!normalized) return "";
        if (/^https?:\/\//i.test(normalized)) return normalized;
        normalized = normalized.replace(/\\/g, "/");
        normalized = normalized.replace(/^https?:\/\/[^/]+/i, "");
        normalized = normalized.replace(/^\/?stock-models\//i, "");
        normalized = normalized.replace(/^\/?models\//i, "");
        normalized = normalized.replace(/^\/?modelfiles\//i, "");
        normalized = normalized.replace(/^\/+/i, "");
        if (!/^2d\//i.test(normalized)) {
            normalized = `2D/${normalized}`;
        }
        if (!/\.pdf$/i.test(normalized)) {
            normalized = `${normalized}.pdf`;
        }
        return `${SERVER_BASE_URL}/stock-models/${normalized}`;
    };

    const handleModelOpen = async () => {
        if (!bestModel) return showModelError();

        setM3DButtonLoading(true);
        try {
            if (bestModel.type === "3D" && bestModel.format === "glb") {
                navigate(`/model-viewer/${machineID}/${extractFilename(bestModel.path)}`);
                return;
            }

            if (bestModel.type === "3D" && bestModel.format === "html") {
                const response = await processModelFile({
                    filename: extractFilename(bestModel.path),
                    machinename: machine_name,
                    currency: selectedSiteCurrency.toUpperCase(),
                    loggedinUser: user.user_id,
                    ecommachineid: machineID,
                    baseColor: theme.palette.primary.main,
                    selectedSite,
                    site_id: selectedSiteData?.site_id || "",
                    cus_id: selectedSiteData?.cus_id || selectedSiteData?.customer_id || "",
                    machinenumber: machine_number,
					...(bestModel.engine === "inhouse" ? { engine: "inhouse" } : {}),
                });
                if (response?.data?.url) {
					if (bestModel.engine === "inhouse" || response.data.kind === "inhouse") {
						const modelUrl = SERVER_BASE_URL + response.data.url;
						if (!(await verifyResourceExists(modelUrl))) {
							showModelError();
							return;
						}
						navigate("/html-model-viewer/" + machineID + "/" + extractFilename(response.data.url), {
							state: {
								modelUrl,
								fromPath: location.pathname + location.search + location.hash,
								machineName: machine_name,
								machineNumber: machine_number,
								kind: "inhouse",
							},
						});
					} else {
						await openUrlIfExists(SERVER_BASE_URL + response.data.url);
					}
                } else {
                    showModelError();
                }
                return;
            }

            if (bestModel.type === "2D") {
                if (EXTERNAL_2D_ENABLED) {
                    const pdfUrl = build2dUrl(bestModel.path);
                    if (await openUrlIfExists(pdfUrl)) return;
                    showModelError();
                    return;
                }
                navigate(`/2d-model-viewer/${machineID}/${extractFilename(bestModel.path)}`);
            }
        } catch (err) {
            console.error("Error fetching the model:", err);
            showModelError();
        } finally {
            setM3DButtonLoading(false);
        }
    };

    // Mirrors Products.js's ?open_bom=1 -> handleOpenBomModal() pattern exactly, for this
    // page's own "View Model" button (which normally requires a click) — lets CHIP
    // (get_part_model_viewer) open the model automatically via ?open_model=1 once this
    // page's machine/model data has loaded, using the SAME decision the button's own
    // onClick already makes (open the picker when this machine has multiple models,
    // otherwise open the single model directly, via handleModelOpen — a part's model
    // behaviour mirrors its machine's overall model set, same as the button below).
    // This is a page-level trigger scoped to this one part's page only — it does not
    // touch MachineCard.js/ModelButton.js.
    const modelAutoOpenHandledRef = useRef(false);
    // Tracks which machineID the currently-loaded machine/model data actually belongs to —
    // set at the end of fetchMachineDetails above. Gating the auto-open effect on
    // machineDetailsLoading alone is NOT sufficient: machineID and location.search change in
    // the SAME render (one navigate() call), so in that first render this effect and the
    // fetchMachineDetails effect both close over the SAME stale pre-navigation
    // machineDetailsLoading value — a setState in one effect can't retroactively change what
    // a sibling effect in the same commit already read (same class of bug fixed in
    // Products.js's machine-model-viewer flow; applying the same fix here pre-emptively since
    // the underlying React effect-timing issue is identical, not something specific to Products.js).
    const modelDataForMachineIdRef = useRef(null);
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const hasParam = params.get("open_model") === "1";
        // User-reported: same never-resets latch as Products.js's modelAutoOpenHandledRef —
        // once this effect successfully auto-opened a model for the FIRST ?open_model=1 seen
        // on this part's page, the ref stayed true forever, so ANY later ?open_model=1 (even
        // for a different part navigated to without a full remount) silently no-opped. Reset
        // the ref back to false whenever the param is absent (Support.js's
        // ?open_create_case=1 pattern) so it only ever guards a genuine same-param re-fire.
        if (!hasParam) {
            modelAutoOpenHandledRef.current = false;
            return;
        }
        if (modelAutoOpenHandledRef.current || machineDetailsLoading) return;
        if (modelDataForMachineIdRef.current !== machineID) return;
        modelAutoOpenHandledRef.current = true;
        // Strip the query param BEFORE calling handleModelOpen below, not after — its
        // glb branch calls navigate() itself (to /model-viewer/...), and since both
        // calls run synchronously in the same tick, whichever navigate() is LAST wins.
        // Stripping first means a real navigate from handleModelOpen is the one left
        // standing, instead of being clobbered by this cleanup navigate.
        params.delete("open_model");
        const newSearch = params.toString();
        navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ""}`, { replace: true });
        // Same branch the "View Model" button's own onClick makes below: popup only when
        // this machine actually has multiple models, otherwise open the single model
        // directly via handleModelOpen (in-app glb viewer / new-tab html / in-app or
        // new-tab 2D depending on EXTERNAL_2D_ENABLED).
        //
        // Deliberately recompute the count HERE from machineModelData directly, instead
        // of reading the hasMultipleModels state variable — hasMultipleModels is set by a
        // SEPARATE downstream effect that only reacts to machineModelData one render-cycle
        // later. This effect's own ref-guard (modelDataForMachineIdRef) only guarantees
        // machineModelData itself is fresh for this machineID, not that hasMultipleModels
        // has caught up to it yet — so on the very first render where this effect becomes
        // eligible to fire, hasMultipleModels can still be the STALE value (often `false`,
        // e.g. left over from the previous machine or the initial default), causing this
        // effect to wrongly take the single-model direct-open branch and permanently mark
        // itself handled (via the ref) before the sibling effect ever corrects
        // hasMultipleModels to `true` — the popup then never gets a second chance to open.
        // Live-reproduced: machine 21686-3003 (13 real 2D models across its BOM) opened
        // handleModelOpen's single-model branch instead of the picker, and errored with
        // "Failed to open model" once bestModel's real 2D path failed openUrlIfExists.
        if (!hasDirectMachineModelsFromModelData(machineModelData)) {
            return;
        }
        if (countAvailableModelsFromModelData(machineModelData) > 1) {
            setModelModalOpen(true);
        } else {
            handleModelOpen();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, machineDetailsLoading, machineModelData, machineID]);

    const handleAddToRequest = async (event) => {
        event.stopPropagation();

        setIsLoading(true);
        setIsSuccess(false);
        try {
            const req_body = {
                machine_id: purchaseTarget?.machine_id || machineID,
                product_id: purchaseTarget?.product_id,
                site_number: selectedSite,
                user_id: user.user_id,
            };

            try {
                const response = await saveRequestList(req_body);

                if (response.status) {
                    setIsSuccess(true);
                    triggerToast(`Product ${purchaseTarget?.product_name || "product"} added to Request List`, "success");
                    setTimeout(() => setIsSuccess(false), 1200);
                } else {
                    triggerToast(`Product ${purchaseTarget?.product_name || "product"} ${t('GENERAL.ALREADY_IN_REQUEST_LIST')}`, "warning");
                }
                setIsLoading(false);
            } catch (error) {
                setIsLoading(false);
                console.error("Error fetching product name:", error);
            }
        } catch (error) {
            setIsSuccess(false);
            setIsLoading(false);
            triggerToast(`${t('GENERAL.UNABLE_TO_ADD_PARTS')} ${purchaseTarget?.product_name || "product"} to Request List`, "error");
        }
    };

    const handleSavedForLater = async (event) => {
        event.stopPropagation();

        setIsLoadingSFL(true);
        try {
            const req_body = {
                machine_id: purchaseTarget?.machine_id || machineID,
                product_id: purchaseTarget?.product_id,
                site_number: selectedSite,
                user_id: user.user_id,
            };

            try {
                if (isSuccessSFL) {
                    const response = await removeFromSavedForLater([purchaseTarget?.product_id], selectedSite);
                    if (response.status) {
                        setIsSuccessSFL(false);
                        triggerToast(`Product ${product.product_number} removed from Saved `, "success");
                    } else {
                        triggerToast(`Product ${product.product_number} already in Saved for Later`, "warning");
                    }
                    setIsLoadingSFL(false);
                } else {
                    const response = await saveProductForLater(req_body);
                    if (response.status) {
                        setIsSuccessSFL(true);
                        triggerToast(`Product ${product.product_number} added to Saved for Later`, "success");
                    } else {
                        triggerToast(`Product ${product.product_number} already in Saved for Later`, "warning");
                    }
                    setIsLoadingSFL(false);
                }
            } catch (error) {
                setIsLoadingSFL(false);
                console.error("Error fetching product name:", error);
            }
        } catch (error) {
            setIsSuccessSFL(false);
            setIsLoadingSFL(false);
            triggerToast(`${t('GENERAL.UNABLE_TO_SAVE')} ${product?.product_number || "product"} for later`, "error");
        }
    };

    const handleAddToCart = async (event) => {
        event.stopPropagation();

        setIsLoading(true);
        setIsSuccess(false);

        try {
            const finalQuantity = normalizeOrderQuantity(quantity, purchaseTarget);
            if (finalQuantity !== quantity) {
                setQuantity(finalQuantity);
                triggerToast(
                    t("ALERT_MESSAGES.QUANTITY_ADJUSTED_TO_MINIMUM", {
                        defaultValue: "Quantity adjusted to minimum order quantity: {{quantity}}",
                        quantity: formatOrderQuantity(minimumSalesOrderQuantity),
                    }),
                    "warning"
                );
            }
            await addToCart({
                ProductID: purchaseTarget?.product_id,
                MachineID: purchaseTarget?.machine_id || machineID,
                Quantity: finalQuantity,
                Site: selectedSite,
                minimum_sales_order_quantity: minimumSalesOrderQuantity,
                multiple_sales_order_quantity: multipleSalesOrderQuantity,
            });

            setIsSuccess(true);
            setIsLoading(false);
            triggerToast(`Product ${purchaseTarget?.product_name || "product"} added to the cart`, "success");
            setTimeout(() => setIsSuccess(false), 2000);
        } catch (error) {
            setIsSuccess(false);
            setIsLoading(false);
            triggerToast(`Unable to add ${purchaseTarget?.product_name || "product"} to the cart`, "error");
        }
    };

    const handleQuantityChange = (event) => {
        const value = event.target.value;
        if (value === "") {
            setQuantity(minimumSalesOrderQuantity);
            return;
        }
        const parsed = parseOrderQuantity(value);
        if (Number.isNaN(parsed)) {
            setQuantity(minimumSalesOrderQuantity);
            return;
        }
        if (
            String(value).length < String(quantity).length &&
            parsed < quantity &&
            multipleSalesOrderQuantity > 0
        ) {
            setQuantity(Math.max(minimumSalesOrderQuantity, quantity - multipleSalesOrderQuantity));
            return;
        }
        const newQuantity = Math.min(effectiveMaxQty, normalizeOrderQuantity(parsed, purchaseTarget));
        setQuantity(newQuantity);
    };
    const handleDocumentationNavigate = () => {
        if (!DOCUMENT_EXTERNAL_ENABLED || !DOCUMENT_EXTERNAL_URL) {
            navigate(`/documentation/${machineID}`, {
                state: {
                    fromMachineCard: true,
                    machine: {
                        machine_id: machineID,
                        machine_name: product?.machine_name || machine_name,
                        machine_number: product?.machine_number || machine_number,
                    },
                },
            });
            return;
        }
        if (!machineDocEntry || machineDocEntry.HasDoc !== "Y") return;
        const username = selectedSiteData?.amf_method_username_ln;
        const password = selectedSiteData?.amf_method_password_ln;
        const loginUrl = buildExternalLoginUrl({
            templateUrl: DOCUMENT_EXTERNAL_URL,
            username,
            password,
        });
        const targetUrl = buildExternalTargetUrl({
            username,
            group: machineDocEntry.Group,
            machine: machineSerialForDocs,
        });
        if (!loginUrl || !targetUrl) {
            navigate(`/documentation/${machineID}`, {
                state: {
                    fromMachineCard: true,
                    machine: {
                        machine_id: machineID,
                        machine_name: product?.machine_name || machine_name,
                        machine_number: product?.machine_number || machine_number,
                    },
                },
            });
            return;
        }
        openExternalDocumentation({ loginUrl, targetUrl });
    };

    useEffect(() => {
        const normalizedQuantity = normalizeOrderQuantity(quantity, purchaseTarget);
        if (canPurchaseTarget && quantity !== normalizedQuantity) {
            setQuantity(normalizedQuantity);
        }
    }, [canPurchaseTarget, purchaseTarget, quantity]);

    useEffect(() => {
        let showAlert = false;
        let alertMessage = "";
        if (quantity > effectiveMaxQty) {
            showAlert = true;
            alertMessage = t('ALERT_MESSAGES.QUANTITY_ADJUSTED_TO_MAX', { MAX_QTY: effectiveMaxQty });
            setQuantity(effectiveMaxQty);
        }
        if (showAlert) {
            triggerToast(alertMessage, "warning");
        }
    }, [quantity, effectiveMaxQty, t]);
    const handleOrdersView = () => {

        navigate(`/order-history/${machineID}/${productID}`);
    };

    const visibleRecommendedProducts = useMemo(
        () => filterRecommendationsNotInCart(recommendedProducts, cartItems || []),
        [recommendedProducts, cartItems, filterRecommendationsNotInCart]
    );

    if (!machineID || !productID) {
        return <PageNotFound />;
    }

    const handleBomPathNavigate = (targetProductId) => {
        if (!targetProductId) return;
        navigate(`/machines/${machineID}/${targetProductId}`);
    };

    const handleReplacementNavigate = () => {
        if (!replacementProduct?.replacement_product_id) return;
        const replacementMachineId =
            replacementProduct.replacement_machine_id || machineID;
        navigate(
            `/machines/${replacementMachineId}/${replacementProduct.replacement_product_id}`
        );
    };

    const handleReplacedByNavigate = () => {
        if (!replacedByProduct?.replaced_product_id) return;
        const replacedMachineId =
            replacedByProduct.replaced_machine_id || machineID;
        navigate(
            `/machines/${replacedMachineId}/${replacedByProduct.replaced_product_id}`
        );
    };
    const containerClassNames = ["product-container"];
    if (isModalView) containerClassNames.push("product-container--modal");
    return (
        <Container className={containerClassNames.join(" ")} maxWidth="md">
            {loading && (
                <Box display="flex" justifyContent="center" className="loading-box">
                    <CircularProgress />
                </Box>
            )}

            {!loading && error && (
                <Alert severity="error" className="error-message">
                    {error}
                </Alert>
            )}

            {!loading && !error && product && (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Box className="product-details">
                        <Box className="product-image-section">
                            <img
                                src={productImage}
                                alt={product.product_name || t('UI_TEXT.DEFAULT_PRODUCT')}
                                className="product-image-single"
                                onError={(e) => {
                                    e.currentTarget.src = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
                                }}
                            />

                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "center",
                                    marginTop: 2,
                                    width: "100%",
                                }}>
                                {bestModel && hasDirectMachineModelsFromModelData(machineModelData) && (
                                    <Button
                                        startIcon={bestModel.type === "3D" ? <ViewInArIcon /> : <DescriptionIcon />}
                                        onClick={() => {
                                            if (hasMultipleModels) {
                                                setModelModalOpen(true);
                                                return;
                                            }
                                            handleModelOpen();
                                        }}
                                        disabled={m3DButtonLoading}
                                        aria-busy={m3DButtonLoading}
                                        sx={{ flex: 1, minHeight: 40 }}>
                                        {m3DButtonLoading ? (
                                            <>
                                                {t('UI_TEXT.LOADING')}
                                                <CircularProgress
                                                    color="inherit"
                                                    size={18}
                                                    sx={{ marginLeft: "6px" }}
                                                />
                                            </>
                                        ) : (
                                            bestModel.type === "3D"
                                                ? t('BUTTON.OPEN_3D_MODEL')
                                                : t('BUTTON.VIEW_2D_MODEL')
                                        )}
                                    </Button>
                                )}
                                {shouldShowDocumentationButton && (
                                    <Button
                                        sx={{ flex: 1 }}
                                        startIcon={<DescriptionIcon />}
                                        onClick={handleDocumentationNavigate}>
                                        {t('BUTTON.DOCUMETATION')}
                                    </Button>
                                )}
                            </Box>
                        </Box>
                        <Box className="product-info-section">
                            <Typography variant="h4" className="product-name">
                                {product.product_name}
                            </Typography>
                            <div className="header-divider"></div>
                            <Typography variant="body1" className="product-description">
                                <b>{formatInlineLabel(t('TABLE_HEADERS.MACHINE_NAME'))}</b> {machine_name} | <b>{formatInlineLabel(t('UI_TEXT.MACHINE'))}</b> {machine_number} | <b>{formatInlineLabel(t('UI_TEXT.PART'))}</b> {product.product_number}
                            </Typography>
                            {alternativePartNumber && (
                                <Alert
                                    severity="warning"
                                    sx={{ alignItems: "center", my: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        This part has been replaced by:
                                    </Typography>
                                    <Typography variant="body2">
                                        <b>Alternative Part Number:</b> {alternativePartNumber}
                                    </Typography>
                                    {replacementProduct?.replacement_product_name && (
                                        <Typography variant="body2">
                                            <b>Part Name:</b> {replacementProduct.replacement_product_name}
                                        </Typography>
                                    )}
                                    {replacementProduct?.replacement_product_id && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            sx={{ mt: 1 }}
                                            onClick={handleReplacementNavigate}>
                                            View Replacement Part
                                        </Button>
                                    )}
                                </Alert>
                            )}
                            {/* {replacedByPartNumber && (
                                <Alert
                                    severity="info"
                                    sx={{ alignItems: "center", my: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        This part replaces:
                                    </Typography>
                                    <Typography variant="body2">
                                        <b>Original Part Number:</b> {replacedByPartNumber}
                                    </Typography>
                                    {replacedByProduct?.replaced_product_name && (
                                        <Typography variant="body2">
                                            <b>Part Name:</b> {replacedByProduct.replaced_product_name}
                                        </Typography>
                                    )}
                                    {replacedByProduct?.replaced_product_id && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            sx={{ mt: 1 }}
                                            onClick={handleReplacedByNavigate}>
                                            View Original Part
                                        </Button>
                                    )}
                                </Alert>
                            )} */}
                            {canShowPrice && canPurchaseTarget && Number.isFinite(Number(purchaseTargetPrice)) && (
                                <>
                                    <Typography variant="h4" color="primary" className="product-price">
                                        {formatCurrency(purchaseTargetPrice, selectedSiteCurrency)}
                                    </Typography>
                                </>
                            )}
                            {canShowPurchaseActions && (
                                <>
                                    <Box className="product-quantity" marginY={2}>
                                        <Typography variant="body2" className="quantity-label">
                                            {t('UI_TEXT.QTY')}
                                        </Typography>
                                        <TextField
                                            type="number"
                                            variant="outlined"
                                            size="small"
                                            value={quantity}
                                            onChange={handleQuantityChange}
                                            onBlur={() => {
                                                setQuantity(Math.min(effectiveMaxQty, normalizeOrderQuantity(quantity, purchaseTarget)));
                                            }}
                                            inputProps={{
                                                min: minimumSalesOrderQuantity,
                                                max: effectiveMaxQty,
                                                step: multipleSalesOrderQuantity || "any",
                                            }}
                                            className="quantity-input"
                                        />
                                        {minimumSalesOrderQuantity > 1 && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontSize: "12px", mt: 0.5 }}
                                            >
                                                {t("UI_TEXT.MINIMUM_ORDER_QUANTITY", {
                                                    defaultValue: "Minimum order quantity: {{quantity}}",
                                                    quantity: formatOrderQuantity(minimumSalesOrderQuantity),
                                                })}
                                            </Typography>
                                        )}
                                        {multipleSalesOrderQuantity > 0 && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontSize: "12px", mt: 0.5 }}
                                            >
                                                {t("UI_TEXT.MULTIPLE_ORDER_QUANTITY", {
                                                    defaultValue: "Multiple order quantity: {{quantity}}",
                                                    quantity: formatOrderQuantity(multipleSalesOrderQuantity),
                                                })}
                                            </Typography>
                                        )}
                                    </Box>
                                </>
                            )}

                            {canShowProductActions && (canPurchaseTarget ? (
                                <>
                                    {orderCount > 0 ? (
                                        <>
                                            <Typography
                                                variant="body1"
                                                sx={{ fontStyle: "italic", fontSize: "12px" }}
                                                className="product-description">
                                                {UI_TEXT_HELPERS.getOrderCountText(orderCount)}
                                                <Tooltip title={t('UI_TEXT.VIEW_ALL_ORDERS_FOR_THIS_PART')}>
                                                    <Link
                                                        onClick={handleOrdersView}
                                                        sx={{ cursor: "pointer" }}
                                                        underline="hover">
                                                        {t('UI_TEXT.VIEW_ORDERS')}
                                                    </Link>
                                                </Tooltip>
                                            </Typography>
                                        </>
                                    ) : (
                                        <Typography
                                            variant="body1"
                                            sx={{ fontStyle: "italic", fontSize: "12px" }}
                                            className="product-description">
                                            {t('UI_TEXT.NO_ORDERS_FOR_THIS_PART')}
                                        </Typography>
                                    )}
                                    <Box>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            sx={{
                                                backgroundColor: "var(--primary-main)",
                                                padding: "8px 15px",
                                                fontWeight: "bolder",
                                                width: "fit-content",
                                                mt: 2,
                                            }}
                                            onClick={handleAddToCart}
                                            disabled={isLoading}
                                            className="button-primary">
                                            {isLoading ? (
                                                <>
                                                    <CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
                                                    {t('BUTTON.ADDING')}
                                                </>
                                            ) : isSuccess ? (
                                                <>
                                                    <CheckCircleIcon sx={{ color: "white", marginRight: 1 }} />
                                                    {t('BUTTON.ADDED')}
                                                </>
                                            ) : (
                                                t('LABELS.ADD_TO_CART')
                                            )}
                                        </Button>

                                        <Button
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                padding: "8px 15px",
                                                fontWeight: "bolder",
                                                mt: 2,
                                                ml: 2,
                                            }}
                                            onClick={handleSavedForLater}
                                            disabled={isLoadingSFL}
                                            startIcon={<BookmarkIcon />}
                                            className="button-outline">
                                            {isLoadingSFL ? (
                                                <>
                                                    <CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
                                                    {t('BUTTON.SAVING')}
                                                </>
                                            ) : isSuccessSFL ? (
                                                <>
                                                    {t('BUTTON.SAVED')}
                                                </>
                                            ) : (
                                                t('BUTTON.SAVE_FOR_LATER')
                                            )}
                                        </Button>
                                    </Box>
                                </>
                            ) : (
                                <>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        sx={{
                                            backgroundColor: "var(--primary-main)",
                                            padding: "8px 15px",
                                            fontWeight: "bolder",
                                            mt: 2,
                                        }}
                                        onClick={handleAddToRequest}
                                        disabled={isLoading}
                                        className="button-primary">
                                        {isLoading ? (
                                            <>
                                                <CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
                                                {t('BUTTON.ADDING')}
                                            </>
                                        ) : isSuccess ? (
                                            <>
                                                <CheckCircleIcon sx={{ color: "white", marginRight: 1 }} />
                                                {t('BUTTON.ADDED')}
                                            </>
                                        ) : (
                                            t('BUTTON.ADD_TO_REQUEST_LIST')
                                        )}
                                    </Button>
                                    <Typography variant="h6" color="error">
                                        {t('UI_TEXT.PRODUCT_UNAVAILABLE')}
                                    </Typography>
                                </>
                            ))}

                            <Divider sx={{ mt: canShowProductActions ? 4 : 1 }} />
                            <Typography variant="body1" className="product-description">
                                <b>{t('UI_TEXT.PART_TYPE')}</b>{" "}
                                {getRsplLabelForCode(product.product_rspl_code, rsplMapping, t('UI_TEXT.OTHER'))}{" "}
                                | <b>{t('UI_TEXT.WHERE_USED')}</b> {machine_name}{" "}
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                    marginTop: 2,
                                    width: "100%",
                                    columnGap: 2,
                                }}>
                                {canAddToList && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            padding: "8px 15px",
                                            fontWeight: "bolder",
                                            width: "fit-content",
                                        }}
                                        onClick={() => setDialogOpen(true)}>
                                        {t('BUTTON.ADD_TO_RECOMMENDATION')}
                                    </Button>
                                )}

                                <ModalWrapper
                                    open={dialogOpen}
                                    setOpen={setDialogOpen}
                                    onClose={handleCloseDialog}
                                    title={t('UI_TEXT.ADD_PRODUCT_TO_LIST')}
                                    content={
                                        <>
                                            {(selectListError || newListError) && (
                                                <Alert severity="error" sx={{ mb: 2 }}>
                                                    {selectListError || newListError}
                                                </Alert>
                                            )}
                                            <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
                                                <InputLabel id="select-list-label">{t('LABELS.SELECT_LIST')}</InputLabel>
                                                <Select
                                                    labelId="select-list-label"
                                                    value={selectedList}
                                                    onChange={handleListChange}
                                                    fullWidth
                                                    label={t('LABELS.SELECT_LIST')}
                                                    size="small"
                                                    MenuProps={{
                                                        slotProps: {
                                                            root: { className: "modal-menu-popover" },
                                                        },
                                                    }}
                                                    error={!!selectListError}
                                                    helperText={selectListError}>
                                                    {lists.map((list) => (
                                                        <MenuItem key={list.id} value={list.id}>
                                                            {list.name}
                                                        </MenuItem>
                                                    ))}
                                                    {canCreateList && <MenuItem value="new">{t('MENU_ITEMS.CREATE_NEW_LIST')}</MenuItem>}
                                                </Select>
                                                {!!selectListError && <FormHelperText error>{selectListError}</FormHelperText>}
                                            </FormControl>
                                            {selectedList === "new" && (
                                                <>
                                                    <TextField
                                                        label={t('LABELS.LIST_NAME')}
                                                        value={newListName}
                                                        onChange={handleNewListNameChange}
                                                        fullWidth
                                                        size="small"
                                                        required
                                                        error={!!newListError}
                                                        helperText={newListError || t('UI_TEXT.ENTER_UNIQUE_NAME')}
                                                        sx={{ mb: 2 }}
                                                    />
                                                    <TextField
                                                        label={t('LABELS.LIST_DESCRIPTION')}
                                                        value={newListDescription}
                                                        size="small"
                                                        onChange={handleNewListDescriptionChange}
                                                        fullWidth
                                                        sx={{ mb: 2 }}
                                                    />
                                                </>
                                            )}
                                        </>
                                    }
                                    actions={
                                        <>
                                            <Button onClick={handleCloseDialog} color="primary" variant="outlined">
                                                {t('BUTTON.CANCEL')}
                                            </Button>
                                            <Button
                                                onClick={handleAddToRecommendationList}
                                                color="primary"
                                                variant="contained"
                                                disabled={listCreationLoading || (!selectedList && selectedList !== 'new') || (selectedList === 'new' && !newListName)}>
                                                {listCreationLoading ? <CircularProgress size={16} sx={{ color: "white" }} /> : "Add"}
                                            </Button>
                                        </>
                                    }
                                />
                            </Box>


                        </Box>
                    </Box>

                    <Box
                        sx={{
                            width: "100%",
                            mt: 1.5,
                            borderRadius: "10px",
                            // boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)",
                            overflow: "hidden", // Ensures children follow border radius
                        }}
                    >
                        <Divider sx={{ my: '10px' }} />
                    {/* Hidden ENTIRELY when there is nothing to suggest — heading, rule and all.
                        Showing "No recommendations available" advertises a feature that had nothing to
                        say, which is worse than silence: it trains people to ignore the panel. Only the
                        loading state and a non-empty list render.

                        Moved ABOVE the Part Description / BOM Tree accordions, and laid out
                        horizontally, 2026-07-29. It previously sat below both accordions as a
                        vertical stack, so reaching it meant scrolling past everything — AMF's
                        review: "you have to go way down". Companion parts are most useful at the
                        moment the customer is looking at the part. */}
                    {canShowAddToCart && (recommendationsLoading || visibleRecommendedProducts.length > 0) && (
                        <Box sx={{ width: "100%", mt: 5 }}>
                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flexStart",
                                justifyContent: "flexStart"
                            }}>
                                <h4 style={{ marginBottom: '4px' }}>{t('UI_TEXT.FREQUENTLY_TOGETHER')}</h4>
                                <div style={{
                                    borderBottom: '4px',
                                    borderColor: 'var(--primary-main) !important',
                                    background: 'var(--primary-main) !important',
                                    backgroundColor: 'var(--primary-main) !important',
                                    borderBottomColor: 'var(--primary-main) !important',
                                    height: '4px',
                                    margin: '2px 0',
                                    width: '90px',
                                }}></div>
                            </div>

                            {recommendationsLoading && (
                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                    <CircularProgress size={20} />
                                    <Typography sx={{ ml: 1 }}>{t('UI_TEXT.LOADING_RECOMMENDATIONS')}</Typography>
                                </Box>
                            )}

                            {!recommendationsLoading && (
                                <Box sx={{ display: 'grid', gap: 1.5, mt: 1, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' } }}>
                                    {visibleRecommendedProducts.map((product) => (
                                        <TopProductListItem
                                            key={product.product_id}
                                            product={product}
                                            machine_id={product.machine_id}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}

                        {/* Part Description hidden: product_description comes from D365 and is redundant here
                        <Accordion
                            defaultExpanded
                            sx={{
                                boxShadow: "none",
                                borderRadius: 0,
                                "&:before": { display: "none" },
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography fontWeight={600}>{t("UI_TEXT.PART_DESCRIPTION")}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body2" color="text.secondary">
                                    {partDescription || t("UI_TEXT.NO_DESCRIPTION_AVAILABLE")}
                                </Typography>
                            </AccordionDetails>
                        </Accordion> */}

                        <Accordion
                            defaultExpanded
                            sx={{
                                boxShadow: "none",
                                borderRadius: 0,
                                "&:before": { display: "none" },
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography fontWeight={600}>{t("UI_TEXT.BOM_TREE_TITLE")}</Typography>
                            </AccordionSummary>

                            <AccordionDetails>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {t("UI_TEXT.BOM_PATH_HELPER")}
                                </Typography>

                                {bomPathError ? (
                                    <Alert severity="error">{t("ALERT_MESSAGES.FAILED_DATA")}</Alert>
                                ) : (
                                    <BomPathView
                                        tree={bomPathTree}
                                        isLoading={bomPathLoading}
                                        emptyLabel={t("UI_TEXT.NO_BOM_PATH_AVAILABLE")}
                                        onNavigateToProduct={handleBomPathNavigate}
                                    />
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </Box>


                </Box>
            )}
            {/* Was gated behind {hasMultipleModels && (...)}, so setModelModalOpen(true) from
                the ?open_model=1 effect above had no component mounted to actually show for a
                single-model part — the modal is now always mounted so CHIP's trigger reliably
                has something to open regardless of model count. */}
            <MultipleModelViewer
                show={modelModalOpen}
                setShow={setModelModalOpen}
                machine_number={machine_number}
                machine_name={machine_name}
                productNumbers={product?.product_number ? [product.product_number] : []}
                machine_id={machineID}
                machine_product_name={product?.product_name}
                modelData={machineModelData}
                highlightProductNumbers={highlightProductNumbers}
            />
        </Container>
    );
};

export default SingleProduct;
